import { commonUtils } from '@/helpers/commonUtils'
import styles from './PdfPage.module.less'
import cls from 'classnames'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useSize } from 'ahooks'

export function useMarkdownFontSize(ref: React.RefObject<HTMLDivElement>, maxHeight: number) {
  const DEFAULT_FONT_SIZE = 24
  // const [ratio, setRatio] = useState(1)
  const ratio = useRef(1)
  const size = useSize(ref.current)
  const [fontsize, setFontsize] = useState<number>(DEFAULT_FONT_SIZE)
  const [calcEnd, setCalcEnd] = useState(false)

  useEffect(() => {
    if (size) {
      const height = size.height
      if (height > maxHeight) {
        ratio.current -= 0.1
        setFontsize(DEFAULT_FONT_SIZE * ratio.current)
        setCalcEnd(false)
      } else {
        setFontsize(DEFAULT_FONT_SIZE * ratio.current)
        setCalcEnd(true)
      }
    }
  }, [size])

  return { fontsize, end: calcEnd }
}

// doc: https://www.cl.cam.ac.uk/~mgk25/iso-paper-ps.txt
const transformMmToCss = (mm: number) => {
  // 1mm = 1/25.4 inch
  const inch = mm / 25.4
  // 1inch = 72pt  pt 是物理像素
  const pt = inch * 72
  // const dpi = window.devicePixelRatio
  // 物理像素 / css 像素 = dpi  这个1.33 并不是 dpi ，这是我手动调试出来的，但是我无法给这个 1.23 一个合理的解释
  const cssPx = pt / 1.31
  return cssPx
}

interface Props {
  children: React.ReactNode
  bgSrc: string
  no: number
}

function PdfPage({ children, bgSrc, no }: Props) {
  /**
   * B2 横版尺寸是 707mm * 500mm ，但是为什么横版不是 A4 呢？
   * NOTE: 这里的 B2 是我参考调试出来的，我也不知道为什么是这个尺寸，
   * A4: 210mm * 297mm
   * B2: 500mm * 707mm
   */

  /*   const A4 = {
    w: 297,
    h: 210,
  }
  const A4Css = {
    w: transformMmToCss(A4.w),
    h: transformMmToCss(A4.h),
  } */
  const B2 = {
    w: 707,
    h: 500,
  }
  const B2Css = {
    w: transformMmToCss(B2.w),
    h: transformMmToCss(B2.h),
  }

  return (
    <div
      className={styles.pdfPage}
      style={{
        width: Math.floor(B2Css.w),
        minHeight: Math.floor(B2Css.h),
        maxHeight: Math.floor(B2Css.h),
        height: Math.floor(B2Css.h),
      }}
    >
      <img className={styles.bg} src={bgSrc} alt='' />
      <div className={styles.content}>{children}</div>
      <BottomLine style={{ position: 'absolute', left: 80, bottom: 26, zIndex: 2 }} no={no} />
    </div>
  )
}

export function BottomLine(props: { className?: string; style?: CSSProperties; no: number }) {
  const noStr = props.no >= 10 ? props.no.toString() : `0${props.no}`
  return (
    <div className={cls(styles.bottomLine, props.className)} style={props.style}>
      <img
        className={styles.pic}
        src={commonUtils.getImagePath('PDFDownload/divider-line.png')}
        alt=''
      />
      <span className={styles.line}></span>
      <span className={styles.no}>{noStr}</span>
    </div>
  )
}

export function PageHeader(props: { project: ProjectModels.ProjectDetail; text?: string }) {
  return (
    <div className={styles.pageHeader}>
      {props.text ? (
        <PageTitle text={props.text} />
      ) : (
        <img
          className={styles.brand}
          src={commonUtils.getImagePath('PDFDownload/PDF-logo.png')}
          alt=''
        />
      )}
      <Timestamp project={props.project} />
    </div>
  )
}

export function PageTitle(props: { text: string }) {
  return <span className={styles.pageTitle}>{props.text}</span>
}

export function Timestamp(props: { project: ProjectModels.ProjectDetail }) {
  return (
    <span className={styles.timestamp}>
      {dayjs(props.project.createdAt * 1000).format('YYYY-MM-DD')}
    </span>
  )
}

export function PeriodBox(props: { period: string; style?: CSSProperties }) {
  return (
    <div className={styles.periodBox} style={props.style}>
      {props.period}
    </div>
  )
}

export default PdfPage
